import { useRef, useState } from 'react';
import { useFrame } from '@react-three/fiber';
import { Text, Float, Stars, Line, Html, Image as DreiImage } from '@react-three/drei';
import * as THREE from 'three';
import { useExperienceStore } from '../state/useExperienceStore';

const SafeImage = ({ url, fallbackUrl, boxScale = [1, 1], ...props }: any) => {
  const [hasError, setHasError] = useState(false);
  return (
    <DreiImage 
      url={hasError ? fallbackUrl : url} 
      onError={() => setHasError(true)}
      scale={boxScale}
      {...props}
    />
  );
};

const LanguageText = ({ text, index, total }: { text: string, index: number, total: number }) => {
  const textRef = useRef<any>(null);
  
  useFrame((state) => {
    const time = state.clock.getElapsedTime();
    // Cycle visibility based on time
    const cycleTime = 2; // seconds per message
    const currentIdx = Math.floor((time / cycleTime) % total);
    
    if (textRef.current) {
      if (currentIdx === index) {
        // Pulsing opacity when active
        textRef.current.fillOpacity = 0.4 + Math.sin(time * 5) * 0.2;
        textRef.current.position.y = THREE.MathUtils.lerp(textRef.current.position.y, 0, 0.1);
      } else {
        textRef.current.fillOpacity = THREE.MathUtils.lerp(textRef.current.fillOpacity, 0, 0.1);
        textRef.current.position.y = THREE.MathUtils.lerp(textRef.current.position.y, -0.5, 0.1);
      }
    }
  });

  return (
    <Text
      ref={textRef}
      fontSize={0.2}
      color="#00ffff"
      font="monospace"
      fillOpacity={0}
      position={[0, -0.5, 0]}
    >
      {`> DATA_STREAM: ${text}`}
    </Text>
  );
};

const Annotation = ({ position, title, text }: { position: [number, number, number], title: string, text: string }) => {
  return (
    <group position={position}>
      <Float speed={2} rotationIntensity={0.1} floatIntensity={0.2}>
        <group>
          {/* Connector Line */}
          <Line
            points={[[0, 0, 0], [-3, 3, 0]]}
            color="#00ffff"
            lineWidth={1.5}
            transparent
            opacity={0.5}
          />
          
          <group position={[-3, 3.5, 0]}>
            <mesh>
              <planeGeometry args={[7, 3.5]} />
              <meshBasicMaterial color="#000000" transparent opacity={0.7} />
              <lineSegments>
                <edgesGeometry args={[new THREE.PlaneGeometry(7, 3.5)]} />
                <lineBasicMaterial color="#00ffff" transparent opacity={0.4} />
              </lineSegments>
            </mesh>
            
            <Text
              position={[-3.2, 1, 0.05]}
              fontSize={0.3}
              color="#00ffff"
              font="monospace"
              anchorX="left"
              fontWeight="bold"
            >
              {title.toUpperCase()}
            </Text>
            <Text
              position={[-3.2, -0.2, 0.05]}
              fontSize={0.18}
              color="white"
              anchorX="left"
              maxWidth={6}
              fillOpacity={0.8}
              lineHeight={1.4}
            >
              {text}
            </Text>
            
            {/* HUD Scanline */}
            <mesh position={[0, -1.6, 0.02]}>
              <planeGeometry args={[6.8, 0.05]} />
              <meshBasicMaterial color="#00ffff" transparent opacity={0.3} />
            </mesh>
          </group>
        </group>
      </Float>
    </group>
  );
};

const TechnicalDiagram = ({ position, title, type }: { position: [number, number, number], title: string, type: 'flow' | 'exploded' | 'comp' }) => {
  return (
    <group position={position}>
      <mesh>
        <planeGeometry args={[14, 9]} />
        <meshBasicMaterial color="#00ffff" transparent opacity={0.03} />
        <lineSegments>
          <edgesGeometry args={[new THREE.PlaneGeometry(14, 9)]} />
          <lineBasicMaterial color="#00ffff" transparent opacity={0.15} />
        </lineSegments>
      </mesh>
      
      <Text position={[0, 5, 0]} fontSize={0.35} color="#00ffff" font="monospace" fontWeight="bold">
        {`[SYSTEM_${title.toUpperCase()}]`}
      </Text>

      {type === 'flow' && (
        <group position={[0, 0, 0.1]}>
          {[ -4.5, 0, 4.5 ].map((x, i) => (
            <group key={i} position={[x, 0, 0]}>
              <mesh>
                <boxGeometry args={[2.5, 2.5, 0.1]} />
                <meshBasicMaterial color="#00ffff" wireframe />
              </mesh>
              {i < 2 && (
                <Line points={[[1.25, 0, 0], [3.25, 0, 0]]} color="#00ffff" lineWidth={1} />
              )}
            </group>
          ))}
          <Text position={[0, -2.5, 0]} fontSize={0.2} color="white" fillOpacity={0.5}>DATA_REALTIME_STREAM</Text>
        </group>
      )}

      {type === 'exploded' && (
        <group position={[0, 0, 0.1]}>
          <mesh position={[0, 2, 0]} rotation={[Math.PI / 4, 0, 0]}>
            <cylinderGeometry args={[1.8, 1.8, 0.1, 32]} />
            <meshBasicMaterial color="#00ffff" wireframe />
          </mesh>
          <mesh position={[0, 0, 0]} rotation={[Math.PI / 4, 0, 0]}>
            <cylinderGeometry args={[2.2, 2.2, 0.8, 32]} />
            <meshBasicMaterial color="#ffffff" wireframe />
          </mesh>
          <mesh position={[0, -2, 0]} rotation={[Math.PI / 4, 0, 0]}>
            <cylinderGeometry args={[1.8, 1.8, 0.1, 32]} />
            <meshBasicMaterial color="#00ffff" wireframe />
          </mesh>
          <Text position={[3, 0, 0]} fontSize={0.2} color="white" anchorX="left" fillOpacity={0.6}>CORE_ASSEMBLY_v4</Text>
        </group>
      )}

      {type === 'comp' && (
        <group position={[0, 0, 0.1]}>
           <mesh scale={[1, 1, 0.1]}>
             <torusGeometry args={[2.5, 0.05, 16, 100]} />
             <meshBasicMaterial color="#00ffff" />
           </mesh>
           <Text fontSize={0.4} color="#00ffff" font="monospace">98% OPT optimized</Text>
        </group>
      )}
    </group>
  );
};

const FloatingMessage = ({ text, position, speed = 1 }: { text: string, position: [number, number, number], speed?: number }) => {
  return (
    <Float speed={speed} rotationIntensity={0.2} floatIntensity={0.5}>
      <Text
        position={position}
        fontSize={0.25}
        color="#00ffff"
        font="monospace"
        anchorX="center"
        fillOpacity={0.4}
      >
        {text}
      </Text>
    </Float>
  );
};

export const PlanetEarth = () => {
  const mode = useExperienceStore((state) => state.mode);
  const planetRef = useRef<THREE.Group>(null);
  const cloudRef = useRef<THREE.Mesh>(null);
  const heroProductRef = useRef<THREE.Group>(null);

  const isActive = mode === 'PLANET' || mode === 'CONTINENT' || mode === 'INTRO' || mode === 'COUNTRY' || mode === 'HUB';

  const multiLanguageWelcomes = [
    "WELCOME TO NEW WORLD",
    "BIENVENIDOS AL NUEVO MUNDO",
    "BIENVENUE DANS EL NOUVEAU MONDE",
    "WILLKOMMEN IN DER NEUEN WELT",
    "BEM-VINDOS AO NOVO MUNDO",
    "BENVENUTI NEL NUOVO MONDO"
  ];

  useFrame((state, delta) => {
    if (planetRef.current) {
      planetRef.current.rotation.y += delta * 0.02;
    }
    if (cloudRef.current) {
      cloudRef.current.rotation.y += delta * 0.035;
      cloudRef.current.rotation.z += delta * 0.01;
    }
    
    if (heroProductRef.current) {
      heroProductRef.current.rotation.y += delta * 0.15;
    }
  });

  if (!isActive) return null;

  const planetPosition = mode === 'HUB' ? [0, -150, -600] : [0, -125, -450];

  return (
    <group>
      <Stars radius={450} depth={100} count={40000} factor={10} saturation={0} fade speed={2.5} />
      
      {/* Background Cinematic Earth */}
      <group position={planetPosition as [number, number, number]} ref={planetRef}>
        {/* Core Body */}
        <mesh>
          <sphereGeometry args={[110, 128, 128]} />
          <meshPhysicalMaterial 
            color="#020814"
            emissive="#001a33"
            emissiveIntensity={0.5}
            roughness={0.2}
            metalness={0.9}
            transmission={0.1}
            thickness={2}
          />
        </mesh>
        
        {/* Cloud/Atmosphere Layer */}
        <mesh scale={1.01} ref={cloudRef}>
          <sphereGeometry args={[110, 64, 64]} />
          <meshPhongMaterial 
            color="#44aaff" 
            transparent 
            opacity={0.1} 
            side={THREE.DoubleSide}
            wireframe
          />
        </mesh>

        {/* Outer Glow Shell */}
        <mesh scale={1.04}>
          <sphereGeometry args={[110, 64, 64]} />
          <meshBasicMaterial color="#00aaff" transparent opacity={0.05} side={THREE.BackSide} />
        </mesh>

        {/* Global Tech Grid Overlay */}
        <mesh scale={1.02}>
          <sphereGeometry args={[110, 80, 80]} />
          <meshBasicMaterial color="#00ffff" wireframe transparent opacity={0.08} />
        </mesh>
      </group>

      {/* Floating Multilingual NASA-style Data Labels */}
      {mode === 'PLANET' && (
        <group position={[0, 0, -20]}>
          <FloatingMessage text="SYNCING_GLOBAL_NODES..." position={[-25, 10, -10]} speed={2} />
          <FloatingMessage text="ENCRYPTED_MALL_DATA_v7" position={[25, -8, -5]} speed={1.5} />
          <FloatingMessage text="LATENCY: 14ms" position={[-15, -12, 0]} speed={3} />
          <FloatingMessage text="ACTIVE_USERS: 8,421,902" position={[15, 15, -15]} speed={1.8} />
          
          <group position={[0, 12, 0]}>
            <Float speed={2} rotationIntensity={0.1} floatIntensity={0.2}>
              <Text
                fontSize={0.6}
                color="#00ffff"
                font="monospace"
                fontWeight="black"
                anchorX="center"
              >
                WELCOME TO NEW WORLD
              </Text>
              <Text
                position={[0, -0.8, 0]}
                fontSize={0.25}
                color="white"
                font="monospace"
                fillOpacity={0.6}
                anchorX="center"
              >
                GLOBAL VIRTUAL MALL OF THE FUTURE
              </Text>
              
              {/* Cycling Sub-message */}
              <group position={[0, -2, 0]}>
                {multiLanguageWelcomes.map((text, i) => (
                  <LanguageText key={i} text={text} index={i} total={multiLanguageWelcomes.length} />
                ))}
              </group>
            </Float>
          </group>
        </group>
      )}

      {/* Main Feature: FUTURE PRODUCT VISUALIZATION */}
      {mode === 'PLANET' && (
        <group position={[0, 0, -30]}>
          <Float speed={1.2} rotationIntensity={0.15} floatIntensity={0.25}>
            <group ref={heroProductRef}>
              {/* SPLIT VISUALIZATION FRAMEWORKS */}
              <group>
                {/* Left Side: Ultra-Realistic Apple-style Presentation */}
                <group position={[-4.5, 0, 0]}>
                  <mesh castShadow>
                    <boxGeometry args={[7, 12, 1.5]} />
                    <meshPhysicalMaterial 
                      color="#0c0c0c" 
                      metalness={0.9} 
                      roughness={0.08} 
                      reflectivity={1}
                      clearcoat={1}
                      clearcoatRoughness={0.02}
                    />
                  </mesh>
                  {/* Subtle Screen Highlight / Real Image */}
                  <group position={[0, 0, 0.76]}>
                    <SafeImage 
                      url="https://images.unsplash.com/photo-1542496658-e33a6d0d50f6?auto=format&fit=crop&q=80&w=800"
                      fallbackUrl="https://picsum.photos/seed/tech/800/1200"
                      boxScale={[6.5, 11.5]}
                      transparent
                      toneMapped={false}
                    />
                  </group>
                  {/* Brand Icon */}
                  <mesh position={[0, 0, 0.8]} scale={0.8}>
                    <ringGeometry args={[0.4, 0.5, 32]} />
                    <meshBasicMaterial color="#00ffff" />
                  </mesh>
                </group>

                {/* Right Side: Engineering Schematic / X-Ray */}
                <group position={[4.5, 0, 0]}>
                  {/* Outer Glass Case */}
                  <mesh>
                    <boxGeometry args={[7, 12, 1.5]} />
                    <meshPhysicalMaterial 
                      color="#00ffff" 
                      transparent 
                      opacity={0.05} 
                      transmission={0.95} 
                      thickness={1.2}
                      metalness={0}
                      roughness={0}
                    />
                  </mesh>
                  <lineSegments>
                    <edgesGeometry args={[new THREE.BoxGeometry(7, 12, 1.5)]} />
                    <lineBasicMaterial color="#00ffff" transparent opacity={0.3} />
                  </lineSegments>

                  {/* Internal Layered Components */}
                  <group position={[0, 0, 0]}>
                    {/* CPU/Neural Unit */}
                    <mesh position={[0, 3, 0]}>
                      <boxGeometry args={[5, 4, 0.5]} />
                      <meshBasicMaterial color="#00ffff" wireframe />
                    </mesh>
                    {/* Energy Cells */}
                    <group position={[0, -2, 0]}>
                      {[ -1.5, 0, 1.5 ].map((x, i) => (
                        <mesh key={i} position={[x, 0, 0]}>
                          <cylinderGeometry args={[0.5, 0.5, 5, 16]} />
                          <meshBasicMaterial color="#ffffff" wireframe />
                        </mesh>
                      ))}
                    </group>
                    {/* Connectivity Array */}
                    <mesh position={[0, -5, 0]}>
                      <planeGeometry args={[6, 0.5]} />
                      <meshBasicMaterial color="#00ffff" transparent opacity={0.5} wireframe />
                    </mesh>
                  </group>
                </group>

                {/* CENTRAL DIVIDER (GLOW) */}
                <mesh position={[0, 0, 1]} rotation={[0, 0, 0]}>
                    <planeGeometry args={[0.1, 14]} />
                    <meshBasicMaterial color="#00ffff" transparent opacity={0.9} blending={THREE.AdditiveBlending} />
                </mesh>
              </group>
            </group>
          </Float>

          {/* High-Contrast Spotlights for Hero Product */}
          <spotLight
            position={[-10, 10, 10]}
            intensity={200}
            angle={0.5}
            penumbra={1}
            color="#ffffff"
            castShadow
          />
          <spotLight
            position={[10, -10, 10]}
            intensity={150}
            angle={0.5}
            penumbra={1}
            color="#00ffff"
          />
          <pointLight position={[0, 0, 5]} intensity={50} color="#00ffff" />

          {/* HUD ANNOTATION BOXES */}
          <Annotation 
            position={[-11, 4, 5]} 
            title="Nano-Carbon Casing" 
            text="Constructed using ultra-dense carbon lattice for maximum structural integrity and weight reduction." 
          />
          <Annotation 
            position={[11, 1.5, 5]} 
            title="Multi-Core Matrix" 
            text="Autonomous processing units dedicated to real-time environment synchronization and haptic response." 
          />
          <Annotation 
            position={[13, -3.5, 5]} 
            title="Fusion Power Cell" 
            text="Sustainable energy density providing up to 48 hours of continuous peak performance." 
          />

          {/* LOWER TECHNICAL DIAGRAMS (FOOTER) */}
          <group position={[0, -18, 0]}>
             <TechnicalDiagram position={[-18, 0, 0]} title="Logic_Flow" type="flow" />
             <TechnicalDiagram position={[0, 0, 0]} title="Architecture" type="exploded" />
             <TechnicalDiagram position={[18, 0, 0]} title="Opt_Comp" type="comp" />
          </group>

          {/* ORBITAL RING HUD */}
          <group position={[0, 0, -5]}>
            <mesh rotation={[Math.PI / 2, 0, 0]}>
              <torusGeometry args={[35, 0.05, 16, 100]} />
              <meshBasicMaterial color="#00ffff" transparent opacity={0.15} />
            </mesh>
            <mesh rotation={[Math.PI / 2, Math.PI / 4, 0]}>
              <ringGeometry args={[30, 30.2, 64]} />
              <meshBasicMaterial color="#00ffff" transparent opacity={0.1} side={THREE.DoubleSide} />
            </mesh>
          </group>
        </group>
      )}

      {/* OVERLAY SCAN GRADIENT */}
      <Html fullscreen pointerEvents="none">
        <div className="w-full h-full bg-[radial-gradient(circle_at_center,transparent_0%,rgba(0,0,0,0.4)_100%)] mix-blend-overlay opacity-50" />
      </Html>
    </group>
  );
};
